import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { useTheme } from 'emotion-theming';
import { animated, useSpring, useTransition } from 'react-spring';
import ResizeObserver from 'react-resize-observer';
import * as Yup from 'yup';
import IcomoonReact from 'icomoon-react';
import { Form } from 'formik';

import UpdateProgramSlide1 from './UpdateProgramSlide1';
import UpdateProgramSlide2 from './UpdateProgramSlide2';
import UpdateProgramSlide3 from './UpdateProgramSlide3';
import NoHoverButton from './NoHoverButton';

import FormHandler from '../../../../../shared/components/FormHandler/FormHandler';
import Spinner from '../../../../../shared/_TBComponents/Spinner';
import Text from '../../../../../shared/_TBComponents/Text';
import { TransitionGlobal } from '../../../../../shared/GlobalStyles';
import iconSet from '../../../../../shared/images/teambuildr-selection.json';
import optimizeImage from '../../../../../shared/utils/imageOptimizer';
import useReseller from '../../hooks/useReseller';

const PAGE_1_FIELDS = [
  'programName',
  'programUrl',
  'shortDescription',
  'longDescription',
  'redirectUrl',
];

const PAGE_2_FIELDS = ['programType', 'calendarOffsetDate'];

const PAGE_3_FIELDS = ['price', 'billingFrequency', 'agreement'];

const PROGRAM_SOURCE_OPTIONS = [
  {
    id: 0,
    label: 'Calendar',
    value: 'calendar',
  },
  {
    id: 1,
    label: 'Program Library',
    value: 'savedWorkout',
  },
];

const DEFAULT_SLIDE_STYLE = { position: 'absolute', width: '100%' };

const FieldError = styled('div')`
  color: ${(props) => props.theme.colors.error || '#ff6600'};
  font-size: 14px;
  margin: 10px 0px;
`;

const FolderTextWrapper = styled('div')`
  align-items: center;
  display: flex;
  flex-direction: column;
  margin-top: -10px;
  min-height: 65px;
  min-width: 250px;
  width: 40%;

  .calendar-icon-text {
    display: flex;
    font-size: 26px;
    font-weight: 300;
    justify-content: center;
    min-width: 300px;
  }
`;

const InnerFormWrapper = styled('div')`
  align-items: center;
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: center;
  width: 100%;

  .divider-line {
    border-top: 2px solid rgb(232, 232, 232);
    display: flex;
    margin-bottom: 10px;
    margin-top: 5px;
    width: calc(100% + 80px);
  }
`;

const ModalWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: center;
  width: 100%;
`;

const NavCircle = styled('div')`
  border: 1px solid ${(props) => props.theme.colors.border || '#979797'};
  border-radius: 100%;
  cursor: pointer;
  display: block;
  height: 10px;
  width: 10px;

  &.active {
    background-color: ${(props) => props.theme.colors.gray || '#d8d8d8'};
    border: 1px solid ${(props) => props.theme.colors.gray || '#d8d8d8'};
    cursor: default;
  }
`;

const NavigationWrapper = styled('div')`
  align-items: center;
  display: flex;
  justify-content: space-between;
  left: 0px;
  padding: 20px;
  position: absolute;
  top: 0px;
  width: 100%;
  z-index: 1;

  .arrow-div {
    cursor: pointer;
  }
`;

const SlideContainer = styled('div')`
  display: block;
  height: ${(props) => `${props.height}px`};
  min-height: 184px;
  position: relative;
  transition: ${TransitionGlobal};
  width: 100%;
  z-index: 1000;
`;

const SliderNav = styled('div')`
  align-items: center;
  display: flex;
  height: 30px;
  justify-content: space-between;
  margin: 0 auto;
  position: relative;
  width: 45px;
  z-index: 10;
`;

const SpinnerContainer = styled('div')`
  display: flex;
  flex: 1;
  height: 100%;
  justify-content: center;
  left: 0;
  margin-top: 40px;
  position: absolute;
  top: 0;
  width: 100%;
  z-index: 0;
`;

const TitleError = styled('div')`
  color: ${(props) => props.theme.colors.error || '#ff6600'};
  display: flex;
  font-size: 14px;
  justify-content: center;
  width: 500px;
`;

const programSchema = Yup.object().shape({
  billingFrequency: Yup.string().required('Required'),
  calendarOffsetDate: Yup.string()
    .when('programType', {
      is: (programType) => programType === 'PRE-BUILT',
      then: Yup.string().required('Required'),
      otherwise: Yup.string(),
    })
    .nullable(),
  longDescription: Yup.string().required('Description is required'),
  price: Yup.string().required('Required'),
  programName: Yup.string().required('Name is required'),
  programType: Yup.string()
    .when('calendarId', {
      is: (calId) => calId,
      then: Yup.string().required('Required'),
      otherwise: Yup.string(),
    })
    .nullable(),
  programUrl: Yup.string().required('A program URL is required'),
  redirectUrl: Yup.string().url('Must be a valid URL'),
  shortDescription: Yup.string().required('Tagline is required'),
});

const UpdateProgramForm = ({ onRequestClose, program, setCurrentRow }) => {
  const theme = useTheme();

  const [activeSlide, setActiveSlide] = useState(0);
  const [calendarOptions, setCalendarOptions] = useState({});
  const [clickedCalendar, setClickedCalendar] = useState(false);
  const [clickedOffsetDate, setClickedOffsetDate] = useState(false);
  const [flattenedCalendars, setFlattenedCalendars] = useState([]);
  const [isMediaUploaded, setIsMediaUploaded] = useState(false);
  const [page1Errors, setPage1Errors] = useState(false);
  const [page2Errors, setPage2Errors] = useState(false);
  const [page3Errors, setPage3Errors] = useState(false);
  const [prevSlide, setPrevSlide] = useState(0);
  const [programType, setProgramType] = useState(null);
  const [savedWorkoutError, setSavedWorkoutError] = useState('');
  const [selectedCalendar, setSelectedCalendar] = useState('');
  const [selectedDate, setSelectedDate] = useState(false);
  const [slideDimensions, setSlideDimensions] = useState({
    height: 0,
    width: 0,
  });
  const [submitClicked, setSubmitClicked] = useState(false);
  const [urlHandled, setUrlHandled] = useState(true);
  const [userProfilePic, setUserProfilePic] = useState(null);

  const slideRef = useRef();

  const { calendars, currentUser, handleUpdateProgram } = useReseller();

  const isProgramUpdating = useSelector(
    (state) => state.reseller.ui.isProgramUpdating,
  );
  const updateProgramError = useSelector(
    (state) => state.reseller.data.updateProgramError,
  );
  const uploadedMedia = useSelector((state) => state.reseller.data.uploadMedia);

  useEffect(() => {
    if (calendars && calendars.length > 0) {
      const flattened = calendars.flatMap((calendar) => [
        calendar,
        ...(calendar.childCalendars || []),
      ]);
      setFlattenedCalendars(flattened);

      if (Object.keys(calendarOptions).length === 0) {
        const newDictionary = flattened.reduce((acc, calendar) => {
          acc[calendar.id] = calendar;
          return acc;
        }, {});
        setCalendarOptions(newDictionary);
      }
    }
  }, [calendars, calendarOptions]);

  useEffect(() => {
    if (activeSlide !== 1 && savedWorkoutError) {
      setSavedWorkoutError('');
    }
  }, [activeSlide, savedWorkoutError]);

  useEffect(() => {
    if (
      program.calendarId
      && calendars?.length > 0
      && !clickedCalendar
      && Object.keys(calendarOptions).length > 0
    ) {
      const cal = calendarOptions[program.calendarId] || {};
      setSelectedCalendar(cal);
    }
  }, [calendarOptions, calendars, clickedCalendar, program.calendarId]);

  useEffect(() => {
    if (program?.mediaKey) {
      const environment = process.env.ENVIRONMENT.toLowerCase() !== 'production'
        ? 'staging'
        : 'production';
      const resellerPic = optimizeImage(
        `https://s3.amazonaws.com/teambuildr-${environment}-assets/${program.mediaKey}`,
        {
          resize: {
            fit: 'contain',
            width: 800,
          },
        },
      );
      setUserProfilePic(resellerPic);
    }
  }, [program]);

  const formAnimation = useSpring({
    opacity: 1,
    zIndex: 1,
  });

  const spinnerAnimation = useSpring({ opacity: 0, zIndex: 0 });

  const fromResolver = () => {
    if (prevSlide === 0 && activeSlide === 1) {
      return 'translate3d(25%, 0, 0)';
    }
    if (prevSlide === 1 && activeSlide === 2) {
      return 'translate3d(25%, 0, 0)';
    }
    return 'translate3d(-25%, 0, 0)';
  };

  const leaveResolver = () => {
    if (prevSlide === 2 && activeSlide === 1) {
      return 'translate3d(-25%, 0, 0)';
    }
    if (prevSlide === 1 && activeSlide === 2) {
      return 'translate3d(-25%, 0, 0)';
    }
    if (prevSlide === 0 && activeSlide === 1) {
      return 'translate3d(-25%, 0, 0)';
    }
    return 'translate3d(25%, 0, 0)';
  };

  const slideTransition = useTransition(activeSlide, null, {
    enter: { opacity: 1, transform: 'translate3d(0, 0, 0)' },
    from: { opacity: 0, transform: fromResolver() },
    leave: { opacity: 0, transform: leaveResolver() },
  });

  const getInitialProgramTypeValue = () => {
    if (program.calendarId) {
      return program.calendarOffsetDate !== ''
        && program.calendarOffsetDate !== null
        ? 'PRE-BUILT'
        : 'ONGOING';
    }
    return null;
  };

  const getPage2Fields = (formValues) => {
    const hasCalendarId = formValues.calendarId !== ''
      && formValues.calendarId !== undefined;
    return hasCalendarId ? PAGE_2_FIELDS : [];
  };

  const validateSavedWorkouts = (formValues) => {
    if (formValues.programSource?.value === 'savedWorkout') {
      const { savedWorkouts } = formValues;
      if (!savedWorkouts || savedWorkouts.length === 0) {
        setSavedWorkoutError('You must select at least one saved workout');
        setPage2Errors(true);
        return false;
      }
    }
    return true;
  };

  const validatePageFields = (errors, fieldsToCheck) => Object.keys(errors).some(
    (errorKey) => fieldsToCheck.includes(errorKey),
  );

  const handleBackNavigation = () => {
    if (activeSlide === 1) {
      setActiveSlide(0);
    } else if (activeSlide === 2) {
      setActiveSlide(1);
    }
  };

  const handleClose = () => {
    onRequestClose();
    setCurrentRow(null);
  };

  const handleNavToSlide1 = (formProps) => {
    if (activeSlide === 0) {
      formProps.validateForm().then((errors) => {
        if (validatePageFields(errors, PAGE_1_FIELDS)) {
          setPage1Errors(true);
        } else {
          setPrevSlide(activeSlide);
          setActiveSlide(1);
        }
      });
    } else {
      setPrevSlide(activeSlide);
      setActiveSlide(1);
    }
  };

  const handleNavToSlide2 = (formProps) => {
    if (activeSlide === 0 || activeSlide === 1) {
      formProps.validateForm().then((errors) => {
        const fieldsToCheck = activeSlide === 0 ? PAGE_1_FIELDS : getPage2Fields(formProps.values);

        if (!validateSavedWorkouts(formProps.values)) {
          if (activeSlide === 0) {
            setPrevSlide(activeSlide);
            setActiveSlide(1);
          }
          return;
        }

        if (validatePageFields(errors, fieldsToCheck)) {
          if (activeSlide === 0) {
            setPage1Errors(true);
          } else {
            setPage2Errors(true);
          }
        } else {
          setPrevSlide(activeSlide);
          setActiveSlide(2);
        }
      });
    } else {
      setPrevSlide(activeSlide);
      setActiveSlide(2);
    }
  };

  const handleNextStep = (formProps) => {
    if (activeSlide === 0) {
      formProps.validateForm().then((errors) => {
        if (validatePageFields(errors, PAGE_1_FIELDS)) {
          setPage1Errors(true);
        } else {
          setPrevSlide(activeSlide);
          setActiveSlide(1);
        }
      });
    } else if (activeSlide === 1) {
      formProps.validateForm().then((errors) => {
        const page2Fields = getPage2Fields(formProps.values);

        if (!validateSavedWorkouts(formProps.values)) {
          return;
        }

        if (validatePageFields(errors, page2Fields)) {
          setPage2Errors(true);
        } else {
          setPrevSlide(activeSlide);
          setActiveSlide(2);
        }
      });
    }
  };

  const handleSubmit = (formProps) => {
    formProps.validateForm().then((errors) => {
      if (validatePageFields(errors, PAGE_3_FIELDS)) {
        setPage3Errors(true);
      } else {
        formProps.setSubmitting(true);
        formProps.submitForm();
        setSubmitClicked(true);
      }
    });
  };

  const handleFormSubmit = (values, { setSubmitting }) => {
    setTimeout(() => {
      const newValues = {
        autoStartProgram: values.autoStartProgram,
        billingFrequency: values.billingFrequency,
        calendarId: values.calendarId || 0,
        descriptionLong: values.longDescription,
        descriptionShort: values.shortDescription,
        groups: values.groups.map((group) => group.id),
        mediaId: uploadedMedia?.id || 0,
        programName: values.programName,
        redirectUrl: values.redirectUrl || '',
        saveInfoIds: values.savedWorkouts.map((workout) => workout.id),
        status: values.status,
      };

      if (values.trialPeriod) {
        newValues.trialPeriod = values.trialPeriod.id || values.trialPeriod;
      } else {
        newValues.trialPeriod = 0;
      }

      if (values.numBillingCycles) {
        newValues.numBillingCycles = values.numBillingCycles.id || values.numBillingCycles;
      } else {
        newValues.numBillingCycles = 0;
      }

      if (values.programType === 'ONGOING') {
        newValues.calendarOffsetDate = '';
      } else {
        newValues.calendarOffsetDate = values.calendarOffsetDate || '';
      }

      newValues.startOnDayOfWeek = values.mondayStart ? 1 : -1;

      handleUpdateProgram({
        accountCode: currentUser.accountCode,
        newProgram: newValues,
        programId: program.id,
      });
      setSubmitting(false);
    });
  };

  const renderSlide = (item, props, formProps) => {
    const slideProps = {
      ref: slideRef,
      style: { ...DEFAULT_SLIDE_STYLE, ...props },
    };

    if (item === 0) {
      return (
        <animated.div {...slideProps}>
          <ResizeObserver
            onResize={(rect) => {
              setSlideDimensions(rect);
            }}
          />
          <UpdateProgramSlide1
            formProps={formProps}
            isMediaUploaded={isMediaUploaded}
            page1Errors={page1Errors}
            program={program}
            setIsMediaUploaded={setIsMediaUploaded}
            setUrlHandled={setUrlHandled}
            setUserProfilePic={setUserProfilePic}
            urlHandled={urlHandled}
            userProfilePic={userProfilePic}
          />
        </animated.div>
      );
    }

    if (item === 1) {
      return (
        <animated.div {...slideProps}>
          <ResizeObserver
            onResize={(rect) => {
              setSlideDimensions(rect);
            }}
          />
          <UpdateProgramSlide2
            clickedCalendar={clickedCalendar}
            clickedOffsetDate={clickedOffsetDate}
            flattenedCalendars={flattenedCalendars}
            formProps={formProps}
            page2Errors={page2Errors}
            program={program}
            programSourceOptions={PROGRAM_SOURCE_OPTIONS}
            programType={programType}
            savedWorkoutError={savedWorkoutError}
            selectedCalendar={selectedCalendar}
            selectedDate={selectedDate}
            setClickedCalendar={setClickedCalendar}
            setClickedOffsetDate={setClickedOffsetDate}
            setProgramType={setProgramType}
            setSavedWorkoutError={setSavedWorkoutError}
            setSelectedCalendar={setSelectedCalendar}
            setSelectedDate={setSelectedDate}
          />
        </animated.div>
      );
    }

    if (item === 2) {
      return (
        <animated.div {...slideProps}>
          <ResizeObserver
            onResize={(rect) => {
              setSlideDimensions(rect);
            }}
          />
          <UpdateProgramSlide3
            formProps={formProps}
            page3Errors={page3Errors}
            program={program}
          />
        </animated.div>
      );
    }

    return null;
  };

  const getInitialValues = () => {
    const programSourceOption = program.programSource
      ? PROGRAM_SOURCE_OPTIONS.find(
        (option) => option.value === program.programSource,
      )
      : { label: 'Program Library', value: 'savedWorkout' };

    return {
      autoStartProgram: program.savedWorkouts?.length
        ? program.savedWorkouts[0].autoStartProgram
        : false,
      billingFrequency: program.billingFrequency,
      calendarId: program.calendarId,
      calendarOffsetDate: program.calendarOffsetDate,
      groups: program.groups,
      longDescription: program.descriptionLong,
      mondayStart: program.startOnDayOfWeek !== null,
      numBillingCycles: program.numBillingCycles,
      price: program.price / 100,
      programName: program.programName,
      programSource: programSourceOption,
      programType: getInitialProgramTypeValue(),
      programUrl: program.urlSlug,
      redirectUrl: program.redirectUrl,
      savedWorkouts: program.savedWorkouts.map((sw) => ({
        id: sw.savedWorkoutId,
        programName: sw.workoutName,
      })),
      shortDescription: program.descriptionShort,
      status: program.status,
      trialPeriod: program.trialPeriod,
    };
  };

  return (
    <ModalWrapper>
      <NavigationWrapper>
        {(activeSlide === 1 || activeSlide === 2) ? (
          <Text className='back arrow-div' onClick={handleBackNavigation}>
            <IcomoonReact iconSet={iconSet} icon='left-arrow' size={15} />
          </Text>
        ) : <div />}
        <Text className='close arrow-div' onClick={handleClose}>
          <IcomoonReact iconSet={iconSet} icon='remove' size={13} />
        </Text>
      </NavigationWrapper>
      <FormHandler
        initialValues={getInitialValues()}
        validationSchema={programSchema}
        onSubmit={handleFormSubmit}
      >
        {(formProps) => (
          <InnerFormWrapper>
            <FolderTextWrapper>
              <Text className='calendar-icon-text'>Update Program</Text>
              {Object.keys(formProps.errors).length > 0 && submitClicked && (
                <TitleError className='error-text'>
                  Some required fields are blank
                </TitleError>
              )}
              {updateProgramError && (
                <TitleError className='error-text'>
                  {updateProgramError}
                </TitleError>
              )}
            </FolderTextWrapper>
            <div className='divider-line' />
            <Form style={{ width: '100%' }}>
              <animated.div style={formAnimation}>
                {formProps.errors.exercise && formProps.touched.exercise && (
                  <FieldError className='text-center'>
                    {formProps.errors.exercise}
                  </FieldError>
                )}
                <SlideContainer height={slideDimensions.height}>
                  {slideTransition.map(({ item, props }) => renderSlide(item, props, formProps))}
                </SlideContainer>
                <SliderNav>
                  <NavCircle
                    className={activeSlide === 0 ? 'active' : ''}
                    onClick={() => {
                      setPrevSlide(activeSlide);
                      setActiveSlide(0);
                    }}
                  />
                  <NavCircle
                    className={activeSlide === 1 ? 'active' : ''}
                    onClick={() => handleNavToSlide1(formProps)}
                  />
                  <NavCircle
                    className={activeSlide === 2 ? 'active' : ''}
                    onClick={() => handleNavToSlide2(formProps)}
                  />
                </SliderNav>
              </animated.div>
              <animated.div style={spinnerAnimation}>
                <SpinnerContainer>
                  <Spinner large />
                </SpinnerContainer>
              </animated.div>
              {activeSlide === 2 && (
                <NoHoverButton
                  bottom
                  className='modal-button2'
                  cta={!isProgramUpdating ? 'Update Program' : 'Updating...'}
                  customColor={theme.colors.green}
                  disabled={isProgramUpdating}
                  fullWidth
                  large
                  noBorder
                  onClick={() => handleSubmit(formProps)}
                  primary
                  square
                  type='button'
                />
              )}
              {(activeSlide === 0 || activeSlide === 1) && (
                <NoHoverButton
                  bottom
                  className='modal-button2'
                  cta='Next Step'
                  customColor={theme.colors.green}
                  fullWidth
                  large
                  noBorder
                  onClick={() => handleNextStep(formProps)}
                  primary
                  square
                  type='button'
                />
              )}
            </Form>
          </InnerFormWrapper>
        )}
      </FormHandler>
    </ModalWrapper>
  );
};

UpdateProgramForm.propTypes = {
  onRequestClose: PropTypes.func.isRequired,
  program: PropTypes.instanceOf(Object).isRequired,
  setCurrentRow: PropTypes.func.isRequired,
};

export default UpdateProgramForm;
